import { Table, Col, Icon } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { useBonusDistributionModalStore } from './BonusDistributionModalStore'
import { BonusDistributionObjective } from './types'
import PerButton from '../Button'
import PerFlexRow from '../FlexRow'
import { PerModal } from '../Modal'
import PerInputNumber from '../PerInputNumber'
import PerText from '../Text'
import { useAuth } from '../../../context/auth-context'
import { useCurrency } from '../../../context/currency-context'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { ObjectiveDistributedAmountInput } from '../../../types'
import { saveObjectivesBonusDistribution } from '../../../modules/appraisal/appraisal-flow/shared/async'
import { parseFloatOnTwoDecimals } from './utils'

const EqualSignIcon = () => {
  return <Icon type="pause" style={{ transform: 'rotate(90deg)' }} />
}

export const BonusDistributionModal: FC = () => {
  const { user } = useAuth()
  const [t] = useTranslation()
  const { currency } = useCurrency()
  const { formatCurrency, formatAmountAsPercentage } = useAmountAndCurrency(user.locale)

  const [
    meetingId,
    objectives,
    isModalOpen,
    closeDistributionModal,
    plannedBonus,
    totalBonus,
    setObjectives,
    distributeBonusEqually,
    paidOutBonus,
  ] = useBonusDistributionModalStore(
    (state) => [
      state.meetingId,
      state.objectives,
      state.isModalOpen,
      state.closeDistributionModal,
      state.plannedBonus,
      state.totalBonus,
      state.setObjectives,
      state.distributeBonusEqually,
      state.paidOutBonus,
    ],
    shallow,
  )

  const distributionAmountAbs = parseFloatOnTwoDecimals(Math.abs(totalBonus - plannedBonus - paidOutBonus))
  const distributionAmount = parseFloatOnTwoDecimals(totalBonus - plannedBonus - paidOutBonus)
  const isDistributionDifferencePositive = totalBonus - plannedBonus - paidOutBonus > 0

  const updateDistribution = (globalId: string, newAmount: number) => {
    setObjectives([
      ...objectives.map((x) => {
        if (x.globalId === globalId) {
          x.changeAmount = newAmount
        }
        return x
      }),
    ])
  }

  const calculateNewBonus = (currentBonus: number, changeAmount: number) => {
    if (isDistributionDifferencePositive) {
      return currentBonus + changeAmount
    }
    return currentBonus - changeAmount
  }

  const handleDistributionSave = async () => {
    const distributionObjectiveInputs: ObjectiveDistributedAmountInput[] = objectives.map(({ globalId, currentBonus, changeAmount }) => ({
      globalId: globalId,
      targetAmount: isDistributionDifferencePositive
        ? parseFloatOnTwoDecimals(currentBonus + changeAmount)
        : parseFloatOnTwoDecimals(currentBonus - changeAmount),
    }))

    if (meetingId) {
      await saveObjectivesBonusDistribution(distributionObjectiveInputs, meetingId)
    }
    closeDistributionModal()
  }

  const distributedAmount = objectives.reduce((sum, { changeAmount }) => sum + changeAmount, 0)
  const totalAmount = objectives.reduce((sum, { currentBonus, changeAmount }) => sum + calculateNewBonus(currentBonus, changeAmount), 0)

  const columns: ColumnProps<BonusDistributionObjective>[] = [
    {
      key: 'targetTitle',
      title: t('appraisalMeeting.distributionModal.table.title'),
      dataIndex: 'title',
      width: '160px',
      render: (title: string) => (
        <PerText fontWeight="bold" color="default" className="line-clamp">
          {title}
        </PerText>
      ),
    },
    {
      key: 'CurrentBonus',
      title: t('appraisalMeeting.distributionModal.table.currentBonus'),
      dataIndex: 'currentBonus',
      className: 'textAlign-center',
      width: '140px',
      render: (currentBonus: number) => {
        return <PerText fontWeight="bold">{formatCurrency(currentBonus, currency)}</PerText>
      },
    },
    { className: 'textAlign-center', render: () => <Icon type={isDistributionDifferencePositive ? 'plus' : 'minus'} /> },
    {
      key: 'distribution',
      title: t('appraisalMeeting.distributionModal.table.distribute', { amount: formatCurrency(distributionAmount, currency) }),
      className: 'textAlign-center',
      width: '140px',
      render: (objective: BonusDistributionObjective) => (
        <PerFlexRow direction="column">
          <PerInputNumber
            type="currency"
            currency={currency}
            min={0}
            max={isDistributionDifferencePositive ? distributionAmountAbs : objective.currentBonus - 1}
            step={100}
            timezoneAndLocale={user}
            value={objective.changeAmount}
            onChange={(val) => {
              if (val !== null && val !== undefined && objective.globalId) {
                updateDistribution(objective.globalId, parseFloatOnTwoDecimals(val))
              }
            }}
          />
          <PerInputNumber
            type="percentage"
            min={0}
            max={
              isDistributionDifferencePositive ? 100 : parseFloatOnTwoDecimals(((objective.currentBonus - 1) / distributionAmountAbs) * 100)
            }
            step={1}
            percentageHasDecimal
            timezoneAndLocale={user}
            value={!!distributionAmountAbs ? parseFloatOnTwoDecimals((objective.changeAmount / distributionAmountAbs) * 100) : 0}
            onChange={(val) => {
              if (val !== null && val !== undefined && objective.globalId) {
                const valueFromPercentage = parseFloatOnTwoDecimals(val * (distributionAmountAbs / 100))
                updateDistribution(objective.globalId, valueFromPercentage)
              }
            }}
          />
        </PerFlexRow>
      ),
    },
    { className: 'textAlign-center', render: () => <EqualSignIcon /> },
    {
      key: 'new bonus',
      title: t('appraisalMeeting.distributionModal.table.newBonus'),
      className: 'textAlign-center',
      width: '140px',
      render: ({ currentBonus, changeAmount }: BonusDistributionObjective) => {
        return <PerText fontWeight="bold">{formatCurrency(calculateNewBonus(currentBonus, changeAmount), currency)}</PerText>
      },
    },
  ]

  return (
    <PerModal
      visible={isModalOpen}
      onCancel={closeDistributionModal}
      title={{
        heading: t('appraisalMeeting.distributionModal.heading'),
        subheading: t(
          `appraisalMeeting.distributionModal.${isDistributionDifferencePositive ? 'subheadingPositive' : 'subheadingNegative'}`,
          { amount: formatCurrency(distributionAmount, currency) },
        ),
      }}
      footer={{ confirmBtn: { onClick: handleDistributionSave }, cancelBtn: { onClick: closeDistributionModal } }}
      size="large"
    >
      <PerFlexRow justify="end" mb="s" mt="s">
        <PerButton
          text={t('appraisalMeeting.distributionModal.table.distributeEqually')}
          color="primary"
          variant="pastel"
          type="button"
          onClick={distributeBonusEqually}
          disabled={!isDistributionDifferencePositive}
        />
      </PerFlexRow>
      <Table
        data-testid="distribution-table"
        dataSource={objectives}
        rowKey={'globalId'}
        columns={columns}
        pagination={false}
        className="small fullWidth"
        footer={() => (
          <PerFlexRow justify="space-between" align="middle">
            <Col span={5}>
              <PerText fontSize="s">{t('appraisalMeeting.distributionModal.table.total')}</PerText>
            </Col>
            <Col span={5}>
              <PerText align="center" fontSize="s">
                {formatCurrency(plannedBonus, currency)}
              </PerText>
            </Col>
            <Col span={2}>
              <PerText align="center" fontSize="s">
                <Icon type={isDistributionDifferencePositive ? 'plus' : 'minus'} />
              </PerText>
            </Col>
            <Col span={6}>
              <PerFlexRow direction="column" justify="center" align="middle">
                <PerText fontSize="s">{formatCurrency(distributedAmount, currency)}</PerText>
                <PerText fontSize="s">
                  {!!distributedAmount && !!distributionAmountAbs
                    ? formatAmountAsPercentage(distributedAmount / distributionAmountAbs)
                    : '0 %'}
                </PerText>
              </PerFlexRow>
            </Col>
            <Col span={2}>
              <PerText align="center" fontSize="s">
                <EqualSignIcon />
              </PerText>
            </Col>
            <Col span={4}>
              <PerText align="center" fontSize="s">
                {formatCurrency(totalAmount, currency)}
              </PerText>
            </Col>
          </PerFlexRow>
        )}
      />
    </PerModal>
  )
}
